import { useBodyScrollLock } from '../../hooks/useBodyScrollLock';
import { useToggle } from '../../hooks/useToggle';
import { useActiveSection } from '../../hooks/useActiveSection';
import { useIsScrolled } from '../../hooks/useIsScrolled';
import { scrollToSection } from '../../utils/scrollToSection';
import { handleLogoKeyDown, handleLinkClick } from '../../utils/eventHandlers';
import logo from 'assets/img/logo.png';

interface MenuItem {
    id: string;
    label: string;
}

interface HeaderProps {
    scrollOffset?: number;
}

const navItems: MenuItem[] = [
        { id: 'hero', label: 'Главная' },
        { id: 'expert-advice', label: 'Советы' },
        { id: 'stages', label: 'Процесс' },
        { id: 'projects', label: 'Портфолио' },
        { id: 'extras', label: 'Разовые задачи' },
    ];

const Header: React.FC<HeaderProps> = ({ scrollOffset = 80 }) => {
    const menuItems = navItems;
    const activeSection = useActiveSection(menuItems.map(item => item.id), scrollOffset);
    const [isMobileMenuOpen, toggleMobileMenu] = useToggle(false);
    const isScrolled = useIsScrolled(50);
    const onLogoActivate = () => scrollToSection(menuItems[0]?.id || 'hero');
    useBodyScrollLock(isMobileMenuOpen);

    return (
        <header className={`header ${isScrolled ? 'header-scrolled' : ''}`}>
            <div className="header__container container">
                <div
                    className="header__logo logo"
                    role="button"
                    tabIndex={0}
                    onClick={onLogoActivate}
                    onKeyDown={(e) => handleLogoKeyDown(e, onLogoActivate)}
                    aria-label="Перейти к началу сайта"
                >
                    <img
                        src={logo} // замените на путь к вашей картинке
                        alt="Логотип Мастерская SPA"
                        style={{ width: 40, height: 40, objectFit: 'contain' }}
                    />
                    <div className='header__logo-text'>
                        <span className="logo-gradient">Мастерская</span>
                        <span>Веб-приложений</span>
                    </div>
                </div>

                <nav className="header__nav" aria-label="Основное меню">
                    <ul className="header__nav-list">
                        {menuItems.map(({ id, label }) => (
                            <li key={id}>
                                <a
                                    href={`#${id}`}
                                    className={`header__nav-link ${activeSection === id ? 'header__nav-link-active' : ''}`}
                                    onClick={(e) => handleLinkClick(e, () => scrollToSection(id))}
                                    aria-current={activeSection === id ? 'page' : undefined}
                                    tabIndex={0}
                                >
                                    {label}
                                </a>
                            </li>
                        ))}
                    </ul>
                    <button
                        className="header__cta"
                        onClick={() => scrollToSection('contact')}
                        aria-label="Перейти к разделу Связаться"
                        type="button"
                    >
                        Связаться
                    </button>
                </nav>

                <button
                    className={`header__burger ${isMobileMenuOpen ? 'header__burger-active' : ''}`}
                    onClick={toggleMobileMenu}
                    aria-label={isMobileMenuOpen ? 'Закрыть меню' : 'Открыть меню'}
                    aria-expanded={isMobileMenuOpen}
                    aria-controls="mobile-menu"
                    type="button"
                >
                    <span className="header__burger-line"></span>
                    <span className="header__burger-line"></span>
                    <span className="header__burger-line"></span>
                </button>
            </div>

            <div
                id="mobile-menu"
                className={`header__mobile-menu ${isMobileMenuOpen ? 'header__mobile-menu-open' : ''}`}
                role="menu"
                aria-label="Мобильное меню"
            >
                <div className="container">
                    <ul className="header__mobile-nav-list" role="none">
                        {menuItems.map(({ id, label }) => (
                            <li key={id} role="presentation">
                                <a
                                    href={`#${id}`}
                                    className="header__mobile-link"
                                    role="menuitem"
                                    onClick={(e) => {
                                        handleLinkClick(e, () => scrollToSection(id));
                                        toggleMobileMenu(); // Закрываем меню после клика
                                    }}
                                    tabIndex={0}
                                >
                                    {label}
                                </a>
                            </li>
                        ))}
                    </ul>
                    <button
                        className="header__cta"
                        onClick={() => {
                            scrollToSection('contact');
                            toggleMobileMenu(); // Закрываем меню после клика
                        }}
                        style={{ marginTop: '1rem', width: '100%' }}
                        aria-label="Перейти к разделу Связаться"
                        type="button"
                    >
                        Связаться
                    </button>
                </div>
            </div>
        </header>
    );
};

export default Header;
